import React, {
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';
import {
  Download,
  Help,
} from '@mui/icons-material';
import BulkUploadContext from '../../services/bulkUploadService';
import { resolveBulkUploadErrorMessage } from '../../services/bulkUploadErrorUtils';
import WhoAmIContext from '../../services/whoami';
import { useBulkUploads } from '../../stores/bulkUploadStore';
import {
  MAX_PREVIEW_ERRORS_PER_CATEGORY,
  normalizeHeaderDefinitions,
  validateCsvFile,
  validateCsvText,
} from './csvValidator';
import { buildFileSizeError } from './validationErrors';
import {
  buildDefaultName,
  buildErrorCsvContent,
  bytesFromMb,
  DEFAULT_MAX_SIZE_MB,
  isFileTooLarge,
  parseDuplicateFileUrl,
} from './bulkUploadUploadPanel.utils';
import BulkUploadPanelDropzone from './BulkUploadPanelDropzone';
import BulkUploadPanelValidationSection from './BulkUploadPanelValidationSection';
import BulkUploadPanelSubmitErrorSection from './BulkUploadPanelSubmitErrorSection';
import BulkUploadPanelFormActionsSection from './BulkUploadPanelFormActionsSection';
import useBulkUploadTemplateConfig, { MISSING_VOW_ERROR_CODE } from './useBulkUploadTemplateConfig';
import useBulkUploadValidation from './useBulkUploadValidation';

const ACCEPTED_VOW_CONFIG_IDS = [
  'vow-pro-licensing@2.0',
  'vow-plv-unmonitored@2.0',
  'vow-pro-licensing-with-adverse-actions@2.0',
  'vow-plv-with-adverse-actions-unmonitored@2.0',
];
const REQUIRED_FIELDS_CATEGORY_KEY = 'requiredFieldsMissing';

const FIELD_LABEL_OVERRIDES = {
  referenceId: 'Reference ID',
  businessId: 'Business ID',
  individualName: 'Individual Name',
  individualEmail: 'Individual Email',
  individualPhone: 'Individual Phone',
  businessName: 'Business Name',
  licenseIdentifier: 'License Number',
  licenseSubtypeOrProfession: 'License Subtype Or Profession',
  jurisdiction: 'Jurisdiction',
  licenseCategory: 'License Category',
};

const readFileText = async (file) => {
  if (typeof file?.text === 'function') {
    return file.text();
  }
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result || '');
    reader.onerror = () => reject(reader.error);
    reader.readAsText(file);
  });
};

const toFieldLabel = (fieldName = '') => {
  if (!fieldName) {
    return 'Field';
  }
  if (FIELD_LABEL_OVERRIDES[fieldName]) {
    return FIELD_LABEL_OVERRIDES[fieldName];
  }
  return fieldName
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

const BIDI_CONTROL_PATTERN = /[\u061C\u200E\u200F\u202A-\u202E\u2066-\u206F]/g;

const normalizeVowLabel = (value = '') => String(value || '')
  .normalize('NFKC')
  .replace(BIDI_CONTROL_PATTERN, '')
  .replace(/\s+/g, ' ')
  .trim();

const buildVowOptions = (vows) => {
  const hasMultipleVows = vows.length > 1;

  return vows.map((vow) => {
    const baseLabel = normalizeVowLabel(vow.description)
      || normalizeVowLabel(vow.vowConfigId);
    return {
      vow,
      label: hasMultipleVows ? `${baseLabel} (${vow.vowId})` : baseLabel,
    };
  });
};

const BulkUploadUploadPanel = ({ onCancel, onSubmit }) => {
  const { getConfiguration, uploadFile } = useContext(BulkUploadContext);
  const uploads = useBulkUploads();
  const whoami = useContext(WhoAmIContext) || {};
  const userName = whoami.name || {};
  const uploadedBy = [userName.firstName, userName.lastName].filter(Boolean).join(' ');
  const {
    configuration,
    availableVows,
    templateLoading,
    templateError,
    ensureMissingVowError,
  } = useBulkUploadTemplateConfig({
    getConfiguration,
    acceptedVowConfigIds: ACCEPTED_VOW_CONFIG_IDS,
  });
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedVowId, setSelectedVowId] = useState('');
  const [batchName, setBatchName] = useState('');
  const [description, setDescription] = useState('');
  const [hasEditedName, setHasEditedName] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitLocked, setIsSubmitLocked] = useState(false);
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [duplicateOverrideRequested, setDuplicateOverrideRequested] = useState(false);
  const [isDragActive, setIsDragActive] = useState(false);
  const [expandedErrorCategories, setExpandedErrorCategories] = useState({});

  const requiredFields = useMemo(
    () => normalizeHeaderDefinitions(configuration?.requiredFields),
    [configuration],
  );
  const existingUploadNames = useMemo(() => (
    uploads
      .flatMap((upload) => [upload?.title, upload?.fileName])
      .map((value) => String(value || '').trim())
      .filter(Boolean)
  ), [uploads]);
  const vowOptions = useMemo(() => buildVowOptions(availableVows), [availableVows]);
  const headerLine = useMemo(() => (
    requiredFields
      .map((field) => field.name)
      .filter(Boolean)
      .join(',')
  ), [requiredFields]);
  const maxSizeMb = configuration?.maximumSizeMb || DEFAULT_MAX_SIZE_MB;
  const maxSizeBytes = useMemo(() => bytesFromMb(maxSizeMb), [maxSizeMb]);
  const {
    validationState,
    isValidating,
    resetValidation,
    validateSelectedFile,
  } = useBulkUploadValidation({
    selectedFile,
    requiredFields,
    maxSizeMb,
    maxSizeBytes,
  });
  const hasValidationResult = validationState.lineNumber > 0
    || validationState.rowCount > 0
    || validationState.invalidRowCount > 0
    || !!validationState.fatalError;
  const isValid = !isValidating
    && !validationState.fatalError
    && validationState.invalidRowCount === 0
    && validationState.rowCount > 0;
  const hasRequiredVow = availableVows.some((vow) => vow.vowId === selectedVowId);
  const nameMissing = hasAttemptedSubmit && !batchName.trim();
  const templateErrorMessage = templateError?.code === MISSING_VOW_ERROR_CODE
    ? templateError.message
    : 'Failed to load CSV template. Please try again.';
  const fieldLabelLookup = useMemo(() => (
    requiredFields.reduce((lookup, field) => {
      if (!field?.name) {
        return lookup;
      }
      lookup[field.name] = toFieldLabel(field.name);
      return lookup;
    }, {})
  ), [requiredFields]);
  const validationErrorCategories = useMemo(() => {
    if (validationState.fatalError) {
      const lineNumber = Number.isFinite(validationState.fatalError.lineNumber)
        && validationState.fatalError.lineNumber > 0
        ? validationState.fatalError.lineNumber
        : Number.isFinite(validationState.lineNumber) && validationState.lineNumber > 0
          ? validationState.lineNumber
          : null;
      return [{
        key: 'fatalError',
        label: 'Upload Error',
        count: 1,
        preview: [{
          lineNumber,
          value: '',
          message: validationState.fatalError.message || 'Validation failed.',
        }],
        collapsible: false,
      }];
    }

    const rawCategories = Array.isArray(validationState.errorCategories)
      ? validationState.errorCategories
      : [];

    return rawCategories
      .map((category) => {
        const isMissingCategory = category.kind === 'missing'
          || category.key === REQUIRED_FIELDS_CATEGORY_KEY;
        const fieldName = category.field
          || (typeof category.key === 'string' && category.key.startsWith('invalid:')
            ? category.key.replace('invalid:', '')
            : '');
        const label = isMissingCategory
          ? 'Required Fields Missing'
          : fieldLabelLookup[fieldName] || toFieldLabel(fieldName);
        const preview = (category.preview || [])
          .slice(0, MAX_PREVIEW_ERRORS_PER_CATEGORY)
          .map((entry) => {
            const lineNumber = Number.isFinite(entry?.lineNumber) && entry.lineNumber > 0
              ? entry.lineNumber
              : null;
            const entryField = entry?.field || fieldName;
            const value = isMissingCategory
              ? fieldLabelLookup[entryField || entry?.value || fieldName]
                || toFieldLabel(entryField || entry?.value || fieldName)
              : String(entry?.value || '');
            const message = entry?.message
              || (isMissingCategory ? 'is required' : 'not in allowed values');
            return { lineNumber, value, message };
          });
        const count = Number.isFinite(category.count) ? category.count : preview.length;
        return {
          key: category.key || label,
          label,
          count,
          preview,
          collapsible: count > preview.length,
        };
      })
      .filter((category) => category.count > 0);
  }, [
    fieldLabelLookup,
    validationState.errorCategories,
    validationState.fatalError,
    validationState.lineNumber,
  ]);
  useEffect(() => {
    setExpandedErrorCategories((previous) => {
      const next = {};
      validationErrorCategories.forEach((category, index) => {
        if (!category.collapsible) {
          next[category.key] = true;
          return;
        }
        if (Object.prototype.hasOwnProperty.call(previous, category.key)) {
          next[category.key] = previous[category.key];
          return;
        }
        next[category.key] = validationErrorCategories.length === 1 && index === 0;
      });
      return next;
    });
  }, [validationErrorCategories]);
  useEffect(() => {
    const selectionIsAvailable = availableVows.some((vow) => vow.vowId === selectedVowId);
    if (!selectionIsAvailable) {
      setSelectedVowId(configuration?.vowId || '');
    }
  }, [availableVows, configuration?.vowId, selectedVowId]);
  const totalValidationErrorCount = useMemo(() => {
    if (validationState.fatalError) {
      return 1;
    }
    if (Number.isFinite(validationState.errorCount) && validationState.errorCount > 0) {
      return validationState.errorCount;
    }
    return validationErrorCategories.reduce((total, category) => total + category.count, 0);
  }, [validationErrorCategories, validationState.errorCount, validationState.fatalError]);
  const validationErrorRowCount = useMemo(() => {
    if (validationState.fatalError) {
      return 1;
    }
    if (validationState.invalidRowCount > 0) {
      return validationState.invalidRowCount;
    }
    if (validationErrorCategories.length === 0) {
      return 0;
    }
    const rows = new Set();
    validationErrorCategories.forEach((category) => {
      category.preview.forEach((entry) => {
        if (entry.lineNumber) {
          rows.add(entry.lineNumber);
        }
      });
    });
    return rows.size || 1;
  }, [validationErrorCategories, validationState.fatalError, validationState.invalidRowCount]);
  const hasValidationErrors = Boolean(
    validationState.fatalError
    || totalValidationErrorCount > 0
    || validationState.invalidRowCount > 0,
  );
  const showDetailFields = Boolean(selectedFile && !hasValidationErrors);
  const validationSummary = useMemo(() => {
    if (isSubmitting) {
      return 'Submitting...';
    }
    if (!isValid) {
      return null;
    }
    const count = validationState.rowCount;
    return `Validated ${count} ${count === 1 ? 'row' : 'rows'}.`;
  }, [isSubmitting, isValid, validationState.rowCount]);
  const duplicateFileError = useMemo(
    () => parseDuplicateFileUrl(submitError),
    [submitError],
  );
  const handleToggleErrorCategory = (categoryKey) => {
    setExpandedErrorCategories((previous) => ({
      ...previous,
      [categoryKey]: !previous[categoryKey],
    }));
  };

  const handleDownloadTemplate = () => {
    if (!headerLine) {
      if (templateError) return;
      return;
    }

    const csvContent = `${headerLine}\n`;
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);

    link.setAttribute('href', url);
    link.setAttribute('download', 'bulk_upload_template.csv');
    link.style.visibility = 'hidden';

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleFileSelection = (file) => {
    resetValidation();
    setSelectedFile(file);
    setHasEditedName(false);
    setIsSubmitLocked(false);
    setHasAttemptedSubmit(false);
    setSubmitError(null);
    setDuplicateOverrideRequested(false);
    setBatchName(file ? buildDefaultName({ fileName: file.name, existingNames: existingUploadNames }) : '');
    setDescription('');
  };

  const handleFileChange = (event) => {
    const file = event.target.files?.[0] || null;
    handleFileSelection(file);
    event.target.value = '';
  };

  const buildErrorDownloadContent = async () => {
    if (!selectedFile) {
      throw new Error('No CSV file selected.');
    }
    if (isFileTooLarge(selectedFile, maxSizeBytes)) {
      return buildErrorCsvContent([], buildFileSizeError(maxSizeMb));
    }
    const maxInvalidRows = Number.POSITIVE_INFINITY;
    let result = null;

    try {
      if (typeof selectedFile.stream !== 'function') {
        const text = await readFileText(selectedFile);
        result = await validateCsvText({
          text,
          headers: requiredFields,
          maxInvalidRows,
        });
      } else {
        result = await validateCsvFile({
          file: selectedFile,
          headers: requiredFields,
          maxInvalidRows,
        });
      }
    } catch (error) {
      return buildErrorCsvContent([], {
        type: error?.type || error?.name || 'VALIDATION_FAILED',
        message: error?.message || 'Unable to build error report.',
        lineNumber: error?.lineNumber,
      });
    }

    return buildErrorCsvContent(result?.invalidRows || [], result?.fatalError || null);
  };

  const handleSubmit = async ({ forceDuplicateOverride = false } = {}) => {
    setHasAttemptedSubmit(true);
    if (!selectedFile || isSubmitting || isValidating || isSubmitLocked) {
      return;
    }
    if (!hasRequiredVow) {
      ensureMissingVowError();
      return;
    }
    let canSubmit = isValid;
    let validatedRowCount = validationState.rowCount;
    let validatedFileHash = validationState.fileHash;
    if (!canSubmit) {
      const validationResult = await validateSelectedFile();
      canSubmit = validationResult.isValid;
      validatedRowCount = validationResult.rowCount;
      validatedFileHash = validationResult.fileHash;
    }
    if (!canSubmit) {
      setIsSubmitLocked(true);
      return;
    }
    if (!batchName.trim()) {
      return;
    }
    setIsSubmitting(true);
    setSubmitError(null);
    try {
      const upload = await uploadFile({
        file: selectedFile,
        title: batchName.trim(),
        description: description.trim(),
        fileHash: (forceDuplicateOverride || duplicateOverrideRequested) ? null : validatedFileHash,
        uploadedBy: uploadedBy || 'Unknown',
        vowId: selectedVowId,
      });
      const enrichedUpload = {
        ...upload,
        totalRowCount: validatedRowCount,
      };
      if (onSubmit) {
        onSubmit(enrichedUpload);
      }
    } catch (error) {
      console.error('Failed to submit bulk upload:', error);
      setSubmitError(resolveBulkUploadErrorMessage(error));
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    if (!selectedFile || hasEditedName) {
      return;
    }
    const defaultName = buildDefaultName({
      fileName: selectedFile.name,
      existingNames: existingUploadNames,
    });
    if (defaultName) {
      setBatchName(defaultName);
    }
  }, [existingUploadNames, hasEditedName, selectedFile]);
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        width: '100%',
        padding: '2rem',
        boxSizing: 'border-box',
      }}
    >
      <Box>
        <Typography
          sx={{
            fontSize: '0.875rem',
            fontWeight: 400,
            color: 'emphasis.light',
            marginBottom: '0.25rem',
          }}
        >
          Professional License Verification
        </Typography>
        <Typography
          sx={{
            fontSize: '1.25rem',
            fontWeight: 700,
            color: 'emphasis.dark',
            marginBottom: '1.5rem',
          }}
        >
          Upload a CSV
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, marginBottom: '1.5rem' }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Button
            variant="outlined"
            startIcon={<Download />}
            onClick={handleDownloadTemplate}
            disabled={templateLoading || !headerLine}
            sx={{
              padding: '0.75rem 1.5rem',
              width: '100%',
              textTransform: 'none',
              fontWeight: 600,
              color: 'primary.main',
              borderColor: 'primary.main',
              whiteSpace: 'nowrap',
              ':hover': {
                borderColor: 'primary.dark',
                backgroundColor: 'action.hover',
              },
            }}
          >
            {templateLoading ? 'Loading...' : 'Download CSV Template'}
          </Button>
        </Box>
        {templateError && (
          <Typography sx={{ fontSize: '0.75rem', color: 'error.main' }}>
            {templateErrorMessage}
          </Typography>
        )}
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, marginBottom: '1.5rem' }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Button
            variant="outlined"
            startIcon={<Help />}
            href={`${process.env.PUBLIC_URL}/bulkUploadDocs/Mesh_-_Bulk_Order_Instructions_-_Professional_License_Verification_PLV_2_0.xlsx`}
            sx={{
              padding: '0.75rem 1.5rem',
              width: '100%',
              textTransform: 'none',
              fontWeight: 600,
              color: 'primary.main',
              borderColor: 'primary.main',
              whiteSpace: 'nowrap',
              ':hover': {
                borderColor: 'primary.dark',
                backgroundColor: 'action.hover',
              },
            }}
          >
            Download Instructions
          </Button>
        </Box>
      </Box>

      {availableVows.length > 1 && (
        <FormControl fullWidth sx={{ marginBottom: '1.5rem' }}>
          <InputLabel id="bulk-upload-vow-select-label">Verification Template</InputLabel>
          <Select
            labelId="bulk-upload-vow-select-label"
            id="bulk-upload-vow-select"
            value={selectedVowId}
            label="Verification Template"
            onChange={(event) => setSelectedVowId(event.target.value)}
            disabled={isSubmitting || isValidating}
          >
            {vowOptions.map(({ vow, label }) => (
              <MenuItem key={vow.vowId} value={vow.vowId}>
                {label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      <BulkUploadPanelDropzone
        isDragActive={isDragActive}
        maxSizeMb={maxSizeMb}
        selectedFile={selectedFile}
        onFileSelection={handleFileSelection}
        onFileChange={handleFileChange}
        setIsDragActive={setIsDragActive}
      />

      <BulkUploadPanelValidationSection
        hasValidationErrors={hasValidationErrors}
        totalValidationErrorCount={totalValidationErrorCount}
        validationErrorRowCount={validationErrorRowCount}
        validationErrorCategories={validationErrorCategories}
        expandedErrorCategories={expandedErrorCategories}
        onToggleErrorCategory={handleToggleErrorCategory}
        isValidating={isValidating}
        selectedFile={selectedFile}
        buildErrorDownloadContent={buildErrorDownloadContent}
      />

      <BulkUploadPanelSubmitErrorSection
        submitError={submitError}
        duplicateFileError={duplicateFileError}
        isSubmitting={isSubmitting}
        isValidating={isValidating}
        onSubmitDuplicate={() => {
          setDuplicateOverrideRequested(true);
          setSubmitError(null);
          setIsSubmitLocked(false);
          void handleSubmit({ forceDuplicateOverride: true });
        }}
      />

      <BulkUploadPanelValidationSection
        hasValidationResult={hasValidationResult}
        validationSummary={validationSummary}
      />

      <BulkUploadPanelFormActionsSection
        showDetailFields={showDetailFields}
        batchName={batchName}
        description={description}
        onBatchNameChange={(value) => {
          setBatchName(value);
          setHasEditedName(true);
        }}
        onDescriptionChange={setDescription}
        nameMissing={nameMissing}
        onCancel={onCancel}
        onSubmit={handleSubmit}
        selectedFile={selectedFile}
        hasRequiredVow={hasRequiredVow}
        isSubmitting={isSubmitting}
        isValidating={isValidating}
        isSubmitLocked={isSubmitLocked}
      />
    </Box>
  );
};

export default BulkUploadUploadPanel;
